import React, { useState, useEffect } from 'react';
import Icon from '../../AppIcon';
import Button from '../../ui/Button';
import Header from '../../ui/Header';
import Sidebar from '../../ui/Sidebar';
import ScoutingHeader from './components/ScoutingHeader';
import LocationTracker from './components/LocationTracker';
import ActiveScoutsList from './components/ActiveScoutsList';
import RecentSubmissions from './components/RecentSubmissions';
import DataQualityMetrics from './components/DataQualityMetrics';
import QuickActionPanel from './components/QuickActionPanel';
import OfflineIndicator from './components/OfflineIndicator';

const RealTimeScoutingInterface = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [syncStatus, setSyncStatus] = useState('synced');
  const [activeTab, setActiveTab] = useState('map');

  // Mock data for scouts
  const activeScouts = [
    {
      id: 1,
      name: 'John Smith',
      location: 'San Francisco, CA',
      status: 'active',
      lastUpdate: '2 mins ago',
      submissions: 24,
      qualityScore: 92,
      avatar: 'JH'
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      location: 'New York, NY',
      status: 'active',
      lastUpdate: '5 mins ago',
      submissions: 31,
      qualityScore: 95,
      avatar: 'SJ'
    },
    {
      id: 3,
      name: 'Michael Chen',
      location: 'Seattle, WA',
      status: 'idle',
      lastUpdate: '15 mins ago',
      submissions: 18,
      qualityScore: 88,
      avatar: 'MC'
    },
    {
      id: 4,
      name: 'Emily Rodriguez',
      location: 'Austin, TX',
      status: 'active',
      lastUpdate: '1 min ago',
      submissions: 27,
      qualityScore: 91,
      avatar: 'ER'
    }
  ];

  // Mock submissions data
  const recentSubmissions = [
    {
      id: 1,
      companyName: 'TechStartup Inc',
      companyLogo: '/api/placeholder/40/40',
      companyLogoAlt: 'TechStartup Logo',
      submittedDate: '2024-11-14',
      location: 'San Francisco, CA',
      qualityScore: 94,
      hasPhotos: true,
      photoCount: 8,
      earnings: 150,
      verificationStatus: 'verified'
    },
    {
      id: 2,
      companyName: 'FinTech Solutions',
      companyLogo: '/api/placeholder/40/40',
      companyLogoAlt: 'FinTech Logo',
      submittedDate: '2024-11-14',
      location: 'New York, NY',
      qualityScore: 87,
      hasPhotos: true,
      photoCount: 5,
      earnings: 120,
      verificationStatus: 'pending'
    },
    {
      id: 3,
      companyName: 'CloudTech Systems',
      companyLogo: '/api/placeholder/40/40',
      companyLogoAlt: 'CloudTech Logo',
      submittedDate: '2024-11-13',
      location: 'Seattle, WA',
      qualityScore: 91,
      hasPhotos: true,
      photoCount: 12,
      earnings: 180,
      verificationStatus: 'verified'
    }
  ];

  // Mock quality metrics
  const qualityMetrics = {
    averageScore: 91,
    verifiedSubmissions: 87,
    pendingReview: 12,
    rejectedSubmissions: 3,
    photoQuality: 89,
    dataAccuracy: 93,
    timeliness: 90,
    completeness: 94
  };

  const tabs = [
    { id: 'map', name: 'Live Map', icon: 'MapPin', description: 'Real-time scout locations and activity' },
    { id: 'scouts', name: 'Active Scouts', icon: 'Users', description: 'Current scouting team and performance' },
    { id: 'submissions', name: 'Submissions', icon: 'Upload', description: 'Recent submissions and status' },
    { id: 'quality', name: 'Quality Metrics', icon: 'BarChart3', description: 'Data quality and performance analytics' }
  ];

  const handleSync = () => {
    setSyncStatus('syncing');
    setTimeout(() => setSyncStatus('synced'), 2000);
  };

  const handleSettings = () => {
    console.log('Open settings');
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'map':
        return (
          <div className="bg-card border border-border rounded-lg p-6">
            <LocationTracker scouts={activeScouts} />
          </div>
        );
      case 'scouts':
        return (
          <div className="space-y-6">
            <ActiveScoutsList scouts={activeScouts} />
          </div>
        );
      case 'submissions':
        return (
          <div className="bg-card border border-border rounded-lg p-6">
            <RecentSubmissions submissions={recentSubmissions} />
          </div>
        );
      case 'quality':
        return (
          <div className="space-y-6">
            <DataQualityMetrics metrics={qualityMetrics} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'} pt-16`}>
        <div className="p-6">
          {/* Offline Indicator */}
          {!isOnline && <OfflineIndicator />}

          {/* Page Header */}
          <div className="mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
                    <Icon name="Radar" size={24} color="white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-foreground">Real-Time Scouting Interface</h1>
                    <p className="text-muted-foreground">Live scout tracking and data submission monitoring</p>
                  </div>
                </div>
              </div>

              <ScoutingHeader 
                isOnline={isOnline}
                syncStatus={syncStatus}
                onSync={handleSync}
                onSettings={handleSettings}
                pendingSubmissions={12}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Main Content */}
            <div className="xl:col-span-3">
              {/* Tab Navigation */}
              <div className="bg-card border border-border rounded-lg mb-6">
                <div className="flex flex-wrap border-b border-border">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-all duration-200 border-b-2 ${
                        activeTab === tab?.id
                          ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <Icon name={tab?.icon} size={16} />
                      <span className="hidden sm:inline">{tab?.name}</span>
                    </button>
                  ))}
                </div>
                
                {/* Tab Description */}
                <div className="p-4 bg-muted/30">
                  <p className="text-sm text-muted-foreground">
                    {tabs?.find(tab => tab?.id === activeTab)?.description}
                  </p>
                </div>
              </div>

              {/* Tab Content */}
              {renderTabContent()}
            </div>

            {/* Sidebar Content */}
            <div className="xl:col-span-1 space-y-6">
              {/* Quick Actions */}
              <QuickActionPanel />

              {/* Stats Card */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-semibold text-foreground mb-4">Session Stats</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Icon name="Users" size={16} className="text-primary" />
                      <span className="text-sm text-muted-foreground">Active Scouts</span>
                    </div>
                    <span className="font-semibold text-foreground">{activeScouts.filter(s => s.status === 'active').length}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Icon name="Upload" size={16} className="text-accent" />
                      <span className="text-sm text-muted-foreground">Today's Submissions</span>
                    </div>
                    <span className="font-semibold text-foreground">{recentSubmissions.length}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Icon name="CheckCircle" size={16} className="text-success" />
                      <span className="text-sm text-muted-foreground">Verified Today</span>
                    </div>
                    <span className="font-semibold text-foreground">{qualityMetrics.verifiedSubmissions}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Icon name="TrendingUp" size={16} className="text-warning" />
                      <span className="text-sm text-muted-foreground">Avg Quality</span>
                    </div>
                    <span className="font-semibold text-foreground">{qualityMetrics.averageScore}%</span>
                  </div>
                </div>
              </div>

              {/* Help Card */}
              <div className="bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20 rounded-lg p-6">
                <h4 className="font-semibold text-foreground mb-2">Need Help?</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Learn how to optimize your scouting workflow and improve data quality.
                </p>
                <Button variant="outline" size="sm" fullWidth>
                  <Icon name="HelpCircle" size={16} className="mr-2" />
                  View Guide
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RealTimeScoutingInterface;
